// Карточка чата по клику на шапку (маршрут /chat/:chatId/profile).
//
// ГРУППА — полноценное редактирование (права по моей роли из GET /chats/:id/members):
//   • аватар: показ + смена (owner/admin) — file input image/* → rest.setChatAvatar (multipart);
//   • название: инлайн-редактирование (owner/admin) → rest.renameChat;
//   • участники: список с ролями; добавить (поиск директории, как NewChatScreen) / удалить
//     (owner/admin; owner'а удалить нельзя — сервер ACL, кнопку тоже не рисуем);
//   • удаление группы со всей историей — ТОЛЬКО owner, с модальным подтверждением →
//     rest.deleteChat → сервер каскадно чистит контент и шлёт chat.deleted всем участникам.
// DIRECT — простой профиль собеседника (имя/должность/отдел), без действий.
//
// Обновлённый Chat после каждой мутации вливается в стор через store.actions.ingestChat (сервер
// возвращает собранный Chat) — список чатов/шапка обновляются сразу, WS chat.updated догонит остальных.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { ChatId, ChatMember, PublicUser, UserId } from '@tele-chat/shared-contracts'
import { Avatar, Button, IconButton, ImageCropper, Modal, Spinner } from '@tele-chat/design-system'
import { useRest } from '../transport/TransportContext.js'
import { useStore, useSelector } from '../store/StoreContext.js'
import { useSession } from '../auth/SessionContext.js'
import { useUserCache } from '../users/UserCacheContext.js'
import { useNotifications } from '../notifications/NotificationsContext.js'
import { strings } from '../i18n/strings.js'
import { profileErrorText } from '../i18n/apiErrors.js'

const section: CSSProperties = {
  padding: 'var(--space-4)',
  borderBottom: '1px solid var(--color-divider)',
}
const sectionTitle: CSSProperties = {
  fontSize: 'var(--font-sm)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  marginBottom: 'var(--space-2)',
}
const row: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) 0',
}
const inputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '8px var(--space-3)',
  borderRadius: 'var(--radius-control)',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-md)',
}

export function GroupInfoScreen(): JSX.Element {
  const { chatId: rawChatId } = useParams()
  const chatId = (rawChatId ?? '') as ChatId
  const rest = useRest()
  const store = useStore()
  const navigate = useNavigate()
  const { self } = useSession()
  const users = useUserCache()
  const { notify } = useNotifications()

  const chat = useSelector((s) => s.select.chat(chatId))

  const [members, setMembers] = useState<ChatMember[] | null>(null)
  const [title, setTitle] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [query, setQuery] = useState('')
  const [candidates, setCandidates] = useState<PublicUser[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setTitle(chat?.title ?? '')
  }, [chat?.title])

  const reloadMembers = useCallback((): void => {
    void rest
      .getChatMembers(chatId)
      .then((list) => {
        setMembers(list)
        for (const m of list) if (users.get(m.userId) === undefined) users.ensure(m.userId)
      })
      .catch(() => setMembers([]))
  }, [rest, chatId, users])

  useEffect(() => {
    if (chat?.type === 'group') reloadMembers()
  }, [chat?.type, reloadMembers])

  // Поиск кандидатов на добавление (директория минус уже-участники) — паттерн NewChatScreen.
  useEffect(() => {
    if (!addOpen) return
    const memberSet = new Set((members ?? []).map((m) => m.userId as string))
    const t = setTimeout(() => {
      void rest
        .listUsers(query.trim().length > 0 ? query.trim() : undefined)
        .then((list) => setCandidates(list.filter((u) => !memberSet.has(u.id as string))))
        .catch(() => setCandidates([]))
    }, 250)
    return () => clearTimeout(t)
  }, [addOpen, query, members, rest])

  const myRole = members?.find((m) => m.userId === self?.id)?.role
  const canEdit = myRole === 'owner' || myRole === 'admin'
  const canDelete = myRole === 'owner'

  const ingest = useCallback(
    (updated: Parameters<typeof store.actions.ingestChat>[0]): void => {
      store.actions.ingestChat(updated)
    },
    [store],
  )

  const saveTitle = useCallback(async (): Promise<void> => {
    const t = title.trim()
    if (t.length === 0 || t === chat?.title) return
    try {
      ingest(await rest.renameChat(chatId, t))
      notify({ kind: 'info', title: strings.groupInfo.saved })
    } catch (err) {
      notify({ kind: 'error', title: profileErrorText(err) })
    }
  }, [title, chat?.title, rest, chatId, ingest, notify])

  // Выбор файла открывает КРОП (квадрат, drag+zoom); на сервер уходит уже обрезанный JPEG 512×512
  // (~десятки КБ) — лимит размера недостижим, HEIC с iPhone декодируется браузером в canvas.
  const onAvatarFile = useCallback((file: File | undefined): void => {
    if (file !== undefined) setCropFile(file)
  }, [])

  const onCropped = useCallback(
    async (blob: Blob): Promise<void> => {
      setCropFile(null)
      setBusy(true)
      try {
        ingest(await rest.setChatAvatar(chatId, blob))
        notify({ kind: 'info', title: strings.groupInfo.avatarUpdated })
      } catch (err) {
        notify({ kind: 'error', title: profileErrorText(err) })
      } finally {
        setBusy(false)
      }
    },
    [rest, chatId, ingest, notify],
  )

  const addMember = useCallback(
    async (userId: UserId): Promise<void> => {
      try {
        ingest(await rest.addMember(chatId, userId))
        notify({ kind: 'info', title: strings.groupInfo.memberAdded })
        reloadMembers()
      } catch (err) {
        notify({ kind: 'error', title: profileErrorText(err) })
      }
    },
    [rest, chatId, ingest, notify, reloadMembers],
  )

  const removeMember = useCallback(
    async (userId: UserId): Promise<void> => {
      try {
        ingest(await rest.removeMember(chatId, userId))
        notify({ kind: 'info', title: strings.groupInfo.memberRemoved })
        reloadMembers()
      } catch (err) {
        notify({ kind: 'error', title: profileErrorText(err) })
      }
    },
    [rest, chatId, ingest, notify, reloadMembers],
  )

  const deleteGroup = useCallback(async (): Promise<void> => {
    setBusy(true)
    try {
      await rest.deleteChat(chatId)
      notify({ kind: 'info', title: strings.groupInfo.deleted })
      navigate('/')
    } catch (err) {
      notify({ kind: 'error', title: profileErrorText(err) })
      setBusy(false)
      setConfirmOpen(false)
    }
  }, [rest, chatId, notify, navigate])

  const memberName = useCallback(
    (id: UserId): string => users.get(id)?.name ?? '…',
    [users],
  )

  const roleLabel = (role: ChatMember['role']): string | null =>
    role === 'owner' ? strings.groupInfo.roleOwner : role === 'admin' ? strings.groupInfo.roleAdmin : null

  const header = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        borderBottom: '1px solid var(--color-divider)',
        background: 'var(--color-surface)',
      }}
    >
      <IconButton icon="back" label={strings.nav.back} onClick={() => navigate(`/chat/${encodeURIComponent(chatId)}`)} />
      <span style={{ flex: 1, fontSize: 'var(--font-lg)', fontWeight: 'var(--weight-semibold)' }}>
        {chat?.type === 'group' ? strings.groupInfo.title : strings.groupInfo.directTitle}
      </span>
    </div>
  )

  if (chat === undefined) {
    return (
      <div data-screen="group-info" style={{ height: '100%' }}>
        {header}
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-5)' }}>
          <Spinner size={20} label={strings.app.loading} />
        </div>
      </div>
    )
  }

  // ── direct: простой профиль собеседника ────────────────────────────────────────────────────────
  if (chat.type !== 'group') {
    const peerId = chat.memberIds.find((id) => id !== self?.id)
    const peer = peerId !== undefined ? users.get(peerId) : undefined
    if (peerId !== undefined && peer === undefined) users.ensure(peerId)
    return (
      <div data-screen="group-info" data-kind="direct" style={{ height: '100%', overflowY: 'auto' }}>
        {header}
        <div style={{ ...section, display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <Avatar name={peer?.name ?? ''} src={peer?.avatarUrl} size={72} colorKey={peerId} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 'var(--font-lg)', fontWeight: 'var(--weight-semibold)' }}>{peer?.name ?? '…'}</div>
            {peer?.title !== undefined || peer?.department !== undefined ? (
              <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-sm)' }}>
                {[peer?.title, peer?.department].filter(Boolean).join(' · ')}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  // ── группа: карточка редактирования ────────────────────────────────────────────────────────────
  return (
    <div data-screen="group-info" data-kind="group" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {header}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* — аватар + название — */}
        <div data-group-section="profile" style={{ ...section, display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <Avatar name={chat.title ?? ''} src={chat.avatarUrl} size={72} colorKey={chat.id} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {canEdit ? (
              <>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <input
                    data-group-title=""
                    style={inputStyle}
                    value={title}
                    maxLength={128}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={strings.groupInfo.name}
                  />
                  <Button variant="primary" disabled={busy || title.trim().length === 0 || title.trim() === chat.title} onClick={() => void saveTitle()}>
                    {strings.settings.save}
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  data-group-avatar-input=""
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    onAvatarFile(e.target.files?.[0])
                    e.target.value = '' // повторный выбор того же файла снова даёт change
                  }}
                />
                <Button variant="ghost" disabled={busy} onClick={() => fileInputRef.current?.click()} style={{ marginTop: 'var(--space-2)' }}>
                  {strings.groupInfo.changeAvatar}
                </Button>
              </>
            ) : (
              <div style={{ fontSize: 'var(--font-lg)', fontWeight: 'var(--weight-semibold)', overflowWrap: 'anywhere' }}>
                {chat.title}
              </div>
            )}
            <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-sm)', marginTop: '4px' }}>
              {strings.chat.membersCount(chat.memberCount)}
            </div>
          </div>
        </div>

        {/* — участники — */}
        <div data-group-section="members" style={section}>
          <div style={sectionTitle}>{strings.groupInfo.members}</div>
          {members === null ? (
            <Spinner size={16} label={strings.app.loading} />
          ) : (
            members.map((m) => (
              <div key={m.userId} data-member={m.userId} style={row}>
                <Avatar name={memberName(m.userId)} src={users.get(m.userId)?.avatarUrl} size={36} colorKey={m.userId} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ overflowWrap: 'anywhere' }}>
                    {memberName(m.userId)}
                    {m.userId === self?.id ? ` ${strings.admin.you}` : ''}
                  </div>
                  {roleLabel(m.role) !== null ? (
                    <div style={{ color: 'var(--color-accent)', fontSize: 'var(--font-xs)' }}>{roleLabel(m.role)}</div>
                  ) : null}
                </div>
                {canEdit && m.role !== 'owner' && m.userId !== self?.id ? (
                  <IconButton icon="close" label={strings.groupInfo.removeMember} onClick={() => void removeMember(m.userId)} />
                ) : null}
              </div>
            ))
          )}

          {canEdit ? (
            addOpen ? (
              <div data-group-add="" style={{ marginTop: 'var(--space-2)' }}>
                <input
                  data-group-add-query=""
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  placeholder={strings.groupInfo.searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                <div style={{ maxHeight: '220px', overflowY: 'auto', marginTop: 'var(--space-2)' }}>
                  {candidates.length === 0 ? (
                    <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-sm)' }}>
                      {strings.empty.directoryEmpty}
                    </div>
                  ) : (
                    candidates.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        data-group-candidate={u.id}
                        onClick={() => void addMember(u.id)}
                        style={{
                          ...row,
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--color-text)',
                          cursor: 'pointer',
                        }}
                      >
                        <Avatar name={u.name} src={u.avatarUrl} size={32} colorKey={u.id} />
                        <span style={{ overflowWrap: 'anywhere' }}>{u.name}</span>
                      </button>
                    ))
                  )}
                </div>
                <Button variant="ghost" onClick={() => setAddOpen(false)}>
                  {strings.composer.cancel}
                </Button>
              </div>
            ) : (
              <Button variant="ghost" data-group-add-toggle="" onClick={() => setAddOpen(true)} style={{ marginTop: 'var(--space-2)' }}>
                {strings.groupInfo.addMember}
              </Button>
            )
          ) : null}
        </div>

        {/* — опасная зона: удаление группы (только owner) — */}
        {canDelete ? (
          <div data-group-section="danger" style={section}>
            <Button variant="danger" data-group-delete="" disabled={busy} onClick={() => setConfirmOpen(true)}>
              {strings.groupInfo.deleteGroup}
            </Button>
          </div>
        ) : null}
      </div>

      <ImageCropper
        file={cropFile}
        onCancel={() => setCropFile(null)}
        onCropped={(b) => void onCropped(b)}
        labels={{
          title: strings.groupInfo.cropTitle,
          done: strings.groupInfo.cropDone,
          cancel: strings.groupInfo.deleteConfirmNo,
          zoom: strings.groupInfo.cropZoom,
        }}
      />

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} label={strings.groupInfo.deleteConfirmTitle}>
        <div data-group-delete-confirm="" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxWidth: '360px' }}>
          <h3 style={{ margin: 0 }}>{strings.groupInfo.deleteConfirmTitle}</h3>
          <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 'var(--font-sm)' }}>
            {strings.groupInfo.deleteConfirmBody}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={busy}>
              {strings.groupInfo.deleteConfirmNo}
            </Button>
            <Button variant="danger" data-group-delete-yes="" disabled={busy} onClick={() => void deleteGroup()}>
              {strings.groupInfo.deleteConfirmYes}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
